import { VersionRange } from '../../models/core/version-range.js';
import {
  DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../models/dto/dto.js';
import { Language } from '../../models/serializable/language.js';
import { LocalizedString } from '../../models/serializable/localized-string.js';
import { MapUtils } from '../../utility/map-utils.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import { Component } from '../core/component.js';
import { ServiceModifier } from './service-modifier.js';

export class ServiceModifierComponent extends Component {
  static readonly TYPE_ID: string = '4179c91733004ed19e6dd5ca5d7d7fff';

  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly names: LocalizedString,
    public readonly isRequired: boolean,
    public readonly isSingleSelect: boolean,
    public readonly modifiers: ServiceModifier[],
  ) {
    super();
  }

  get isOptional(): boolean {
    return !this.isRequired;
  }

  get isMultiSelect(): boolean {
    return !this.isSingleSelect;
  }

  get $typeId(): string {
    return ServiceModifierComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(ServiceModifierComponent.SCHEMA_VERSION);
  }

  get $removable(): boolean {
    return false;
  }

  static fromMap(context: SerializationContext, map: SerializedData): ServiceModifierComponent {
    return new ServiceModifierComponent(
        context.fromData<LocalizedString>(MapUtils.getRaw(map, 'names') as SerializedData),
        MapUtils.get<boolean>(map, 'isRequired'),
        MapUtils.get<boolean>(map, 'isSingleSelect'),
        MapUtils.getList<ServiceModifier>(map, 'modifiers', (value) =>
          context.fromData<ServiceModifier>(value as SerializedData),
        ),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'names': context.toData(this.names),
      'isRequired': this.isRequired,
      'isSingleSelect': this.isSingleSelect,
      'modifiers': this.modifiers.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new ServiceModifierComponent(
        this.names.clone(),
        this.isRequired,
        this.isSingleSelect,
        this.modifiers.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: ServiceModifierComponent): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.names, this.names) &&
      other.isRequired === this.isRequired &&
      other.isSingleSelect === this.isSingleSelect &&
      isSameList(other.modifiers, this.modifiers, isSameData);
  }
}

export class ServiceModifierComponentBuilder extends DataModelBuilder<ServiceModifierComponent> {
  build(context: SerializationContext, map: SerializedData): ServiceModifierComponent {
    return ServiceModifierComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(ServiceModifier.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ServiceModifier.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      const name = MapUtils.getOrNull<string>(map, 'name');

      //  This can happen because of a broken migration where V2 models were incorrectly saved with V1 schema version.
      if(isNotNullOrUndefined(name)) {
        const language = context.storage.get<Language>('language', Language.English) ?? Language.English;
        MapUtils.setIfNullOrUndefined(map, 'names', context.toData(LocalizedString.forLanguage(language, name)));
      }

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
